/**
 * AI Citation Tracker - Track when AI engines cite OriginSelect pages
 * 
 * Detects traffic from AI assistants (ChatGPT, Perplexity, Bing Copilot,
 * Google AI Overviews, Claude, etc.) via referrer and user-agent analysis.
 * 
 * Storage: localStorage + optional server-side beacon (no GA4 dependency)
 * 
 * @module aiCitationTracker
 */

// ─── AI Source Definitions ─────────────────────────────────────────────────────

/**
 * Known AI referrer domains and their display names.
 * When a user clicks through from an AI assistant, document.referrer will
 * contain one of these domains.
 */
const AI_REFERRER_SOURCES = [
  // OpenAI / ChatGPT
  { pattern: 'chatgpt.com',            source: 'ChatGPT',            engine: 'openai' },
  { pattern: 'chat.openai.com',        source: 'ChatGPT',            engine: 'openai' },
  { pattern: 'openai.com',             source: 'OpenAI',             engine: 'openai' },

  // Perplexity
  { pattern: 'perplexity.ai',          source: 'Perplexity',         engine: 'perplexity' },

  // Microsoft Copilot / Bing Chat / Bing AI Search
  { pattern: 'copilot.microsoft.com',  source: 'Microsoft Copilot',  engine: 'microsoft' },
  { pattern: 'bing.com/chat',          source: 'Bing Chat',          engine: 'microsoft' },
  { pattern: 'edgeservices.bing.com',  source: 'Bing Copilot',       engine: 'microsoft' },

  // Google Gemini / AI Overviews
  { pattern: 'gemini.google.com',      source: 'Google Gemini',      engine: 'google' },
  { pattern: 'bard.google.com',        source: 'Google Bard',        engine: 'google' },

  // Anthropic / Claude
  { pattern: 'claude.ai',             source: 'Claude',              engine: 'anthropic' },

  // Other AI search engines
  { pattern: 'you.com',               source: 'You.com',             engine: 'you' },
  { pattern: 'phind.com',             source: 'Phind',               engine: 'phind' },
  // kagi.com and search.brave.com are not listed: their referrers come from
  // ordinary web results and cannot be told apart from AI answers.

  // Meta AI
  { pattern: 'meta.ai',               source: 'Meta AI',             engine: 'meta' },

  // AI-powered aggregators
  { pattern: 'consensus.app',         source: 'Consensus',           engine: 'consensus' },
  { pattern: 'elicit.com',            source: 'Elicit',              engine: 'elicit' },
];

/**
 * Known AI bot user-agent strings.
 * These crawl pages to build AI knowledge bases — tracking them tells us
 * which AI engines are indexing our content (potential future citations).
 */
const AI_BOT_USER_AGENTS = [
  // OpenAI
  { pattern: 'ChatGPT-User',     source: 'ChatGPT',           engine: 'openai',     type: 'fetcher' },
  { pattern: 'GPTBot',           source: 'GPTBot',             engine: 'openai',     type: 'crawler' },
  { pattern: 'OAI-SearchBot',    source: 'OpenAI Search',      engine: 'openai',     type: 'crawler' },

  // Anthropic
  { pattern: 'ClaudeBot',        source: 'ClaudeBot',          engine: 'anthropic',  type: 'crawler' },
  { pattern: 'anthropic-ai',     source: 'Anthropic',          engine: 'anthropic',  type: 'crawler' },

  // Perplexity
  { pattern: 'PerplexityBot',    source: 'PerplexityBot',      engine: 'perplexity', type: 'crawler' },

  // Google AI (Google-Extended is specifically for AI training)
  { pattern: 'Google-Extended',  source: 'Google AI',          engine: 'google',     type: 'crawler' },
  // Googlebot and Bingbot are traditional search crawlers — NOT tracked (not AI citations)

  // Amazon
  { pattern: 'Amazonbot',        source: 'Amazonbot',          engine: 'amazon',     type: 'crawler' },

  // ByteDance
  { pattern: 'Bytespider',       source: 'Bytespider',         engine: 'bytedance',  type: 'crawler' },

  // Apple
  { pattern: 'Applebot',         source: 'Applebot',           engine: 'apple',      type: 'crawler' },

  // Common Crawl (powers many AI models)
  { pattern: 'CCBot',            source: 'Common Crawl',       engine: 'commoncrawl', type: 'crawler' },

  // Meta
  { pattern: 'FacebookBot',     source: 'Meta AI',             engine: 'meta',       type: 'crawler' },
  { pattern: 'meta-externalagent', source: 'Meta AI',          engine: 'meta',       type: 'crawler' },
];

/**
 * URL parameters that indicate AI-sourced traffic, mapped to a specific engine.
 * Some AI engines append tracking params when users click through; some of our
 * own outbound links also carry utm_source values like "chatgpt-search" or
 * "claude-search" (see scripts/generate-baby-registry-ai-urls.js).
 */
const AI_URL_PARAM_SOURCES = [
  { param: 'utm_source', values: ['chatgpt-search', 'chatgpt'],  source: 'ChatGPT',            engine: 'openai' },
  { param: 'utm_source', values: ['perplexity'],                 source: 'Perplexity',         engine: 'perplexity' },
  { param: 'utm_source', values: ['bing_chat'],                  source: 'Bing Chat',           engine: 'microsoft' },
  { param: 'utm_source', values: ['copilot'],                    source: 'Microsoft Copilot',   engine: 'microsoft' },
  { param: 'utm_source', values: ['google-ai-overview'],         source: 'Google AI Overview',  engine: 'google' },
  { param: 'utm_source', values: ['gemini'],                     source: 'Google Gemini',       engine: 'google' },
  { param: 'utm_source', values: ['claude-search', 'claude'],    source: 'Claude',              engine: 'anthropic' },
  { param: 'utm_source', values: ['kagi'],                       source: 'Kagi',                engine: 'kagi' },
  { param: 'ref',        values: ['chatgpt'],                    source: 'ChatGPT',             engine: 'openai' },
  { param: 'ref',        values: ['perplexity'],                 source: 'Perplexity',          engine: 'perplexity' },
];

/**
 * Generic fallback for AI-ish params that don't match a known engine above —
 * e.g. a utm_source containing "ai" that isn't one of our recognized platforms.
 */
const AI_URL_PARAM_FALLBACKS = [
  { param: 'utm_source', values: ['ai'], source: 'UTM Tagged' },
  { param: 'ref',        values: ['ai'], source: 'Ref Tagged' },
];


// ─── Storage Keys ──────────────────────────────────────────────────────────────

const STORAGE_KEY = 'os_ai_citations';
const STORAGE_STATS_KEY = 'os_ai_citation_stats';
const MAX_STORED_CITATIONS = 500;  // Rolling window
const SESSION_ATTRIBUTION_KEY = 'os_ai_session_source';

let hasCheckedThisPageLoad = false;


// ─── Detection Functions ───────────────────────────────────────────────────────

/**
 * Detect if the current visit came from an AI referrer
 * @returns {{ detected: boolean, source: string, engine: string, referrer: string } | null}
 */
export function detectAIReferrer() {
  const referrer = document.referrer;
  if (!referrer) return null;

  const referrerLower = referrer.toLowerCase();
  for (const def of AI_REFERRER_SOURCES) {
    if (referrerLower.includes(def.pattern)) {
      return {
        detected: true,
        method: 'referrer',
        source: def.source,
        engine: def.engine,
        referrer,
      };
    }
  }

  // Google AI Overviews are not detectable from the referrer. udm=14 is
  // Google's plain "Web" results filter, so it is not treated as AI traffic.

  return null;
}

/**
 * Detect if the current visitor is an AI bot/crawler
 * @returns {{ detected: boolean, source: string, engine: string, type: string } | null}
 */
export function detectAIBot() {
  const ua = navigator.userAgent;
  if (!ua) return null;

  for (const def of AI_BOT_USER_AGENTS) {
    if (ua.includes(def.pattern)) {
      return {
        detected: true,
        method: 'user-agent',
        source: def.source,
        engine: def.engine,
        botType: def.type,
        userAgent: ua,
      };
    }
  }
  return null;
}

/**
 * Detect AI traffic via URL parameters
 * @returns {{ detected: boolean, source: string, engine?: string, param: string, value: string } | null}
 */
export function detectAIURLParams() {
  const params = new URLSearchParams(window.location.search);

  for (const def of AI_URL_PARAM_SOURCES) {
    const value = params.get(def.param);
    if (value && def.values.some(v => value.toLowerCase().includes(v))) {
      return {
        detected: true,
        method: 'url-param',
        source: def.source,
        engine: def.engine,
        param: def.param,
        value,
      };
    }
  }

  // Fallback values must appear as a whole token ("ai", "ai-search",
  // "chat_ai"), not as a substring of words like "email" or "mailchimp"
  for (const def of AI_URL_PARAM_FALLBACKS) {
    const value = params.get(def.param);
    const tokens = value ? value.toLowerCase().split(/[^a-z0-9]+/) : [];
    if (def.values.some(v => tokens.includes(v))) {
      return {
        detected: true,
        method: 'url-param',
        source: def.source,
        param: def.param,
        value,
      };
    }
  }

  return null;
}

/**
 * Run all detection methods and return the first match
 * @returns {object | null} Detection result or null if not AI traffic
 */
export function detectAICitation() {
  return detectAIReferrer() || detectAIURLParams() || detectAIBot();
}


// ─── Storage Functions ─────────────────────────────────────────────────────────

/**
 * Get stored citations from localStorage
 * @returns {Array} Array of citation events
 */
export function getStoredCitations() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    console.warn('[AI Citation Tracker] Failed to read stored citations:', e);
    return [];
  }
}

/**
 * Get aggregated citation stats
 * @returns {object} Stats summary
 */
export function getCitationStats() {
  try {
    const raw = localStorage.getItem(STORAGE_STATS_KEY);
    return raw ? JSON.parse(raw) : { totalCitations: 0, bySource: {}, byEngine: {}, byPage: {}, firstSeen: null, lastSeen: null };
  } catch (e) {
    return { totalCitations: 0, bySource: {}, byEngine: {}, byPage: {}, firstSeen: null, lastSeen: null };
  }
}

/**
 * Save a citation event to localStorage + update rolling stats
 */
function saveCitation(citation) {
  try {
    // 1. Append to citation log (with rolling limit)
    const citations = getStoredCitations();
    citations.push(citation);
    
    // Keep only the latest N entries
    if (citations.length > MAX_STORED_CITATIONS) {
      citations.splice(0, citations.length - MAX_STORED_CITATIONS);
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(citations));

    // 2. Update aggregate stats (these never get trimmed)
    const stats = getCitationStats();
    stats.totalCitations = (stats.totalCitations || 0) + 1;
    stats.lastSeen = citation.timestamp;
    if (!stats.firstSeen) stats.firstSeen = citation.timestamp;

    // By source
    stats.bySource[citation.source] = (stats.bySource[citation.source] || 0) + 1;

    // By engine
    stats.byEngine[citation.engine] = (stats.byEngine[citation.engine] || 0) + 1;

    // By page (top 50 pages)
    const pagePath = citation.pagePath || citation.page;
    if (pagePath) {
      stats.byPage[pagePath] = (stats.byPage[pagePath] || 0) + 1;
      // Trim to top 50 pages by citation count
      const pageEntries = Object.entries(stats.byPage).sort((a, b) => b[1] - a[1]);
      if (pageEntries.length > 50) {
        stats.byPage = Object.fromEntries(pageEntries.slice(0, 50));
      }
    }

    localStorage.setItem(STORAGE_STATS_KEY, JSON.stringify(stats));
  } catch (e) {
    console.warn('[AI Citation Tracker] Failed to save citation:', e);
  }
}


// ─── Beacon / Server Reporting ─────────────────────────────────────────────────

/**
 * Send citation event to your own analytics backend.
 * Uses navigator.sendBeacon() for reliability (fires even on page unload).
 * Falls back to fetch().
 * 
 * @param {object} citation - The citation event data
 */
function sendCitationBeacon(citation) {
  // In production, use relative URL so the request goes to same-origin (www.originselect.com)
  // CloudFront routes /api/* to Elastic Beanstalk
  // In development, setupProxy.js proxies /api/* to backend
  // NEVER use REACT_APP_API_BASE_URL here — cross-origin sendBeacon with JSON triggers
  // CORS preflight which sendBeacon cannot handle, causing silent failures.
  const endpoint = '/api/ai-citations/track';

  // Always log to console for visibility
  console.log(
    `%c🤖 AI Citation Detected%c ${citation.source} → ${citation.pagePath}`,
    'background: #6366f1; color: white; padding: 2px 6px; border-radius: 3px; font-weight: bold;',
    'color: #6366f1; font-weight: bold;'
  );
  console.table({
    Source: citation.source,
    Engine: citation.engine,
    Method: citation.method,
    Page: citation.pagePath,
    Referrer: citation.referrer || '—',
    Time: citation.timestamp,
  });

  const payload = JSON.stringify({
    ...citation,
    event: 'ai_citation',
    event_category: 'ai_traffic',
  });

  // Use fetch instead of sendBeacon to avoid CORS preflight issues
  // keepalive ensures the request completes even if the page is closing
  console.log('[AI Citation Debug] Sending fetch to:', endpoint, 'payload:', payload);
  fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: payload,
    keepalive: true,
  }).then(resp => {
    console.log('[AI Citation Debug] Fetch response:', resp.status, resp.statusText);
    return resp.json();
  }).then(data => {
    console.log('[AI Citation Debug] Fetch data:', data);
  }).catch(err => {
    console.warn('[AI Citation Tracker] Beacon failed:', err);
  });
}

// ─── Main Tracking Function ────────────────────────────────────────────────────

/**
 * Check if the current page visit is from an AI source and track it.
 * Call this once per page load (e.g., in a useEffect or initialization script).
 * 
 * @returns {object | null} The citation data if detected, null otherwise
 */
export function trackAICitation() {
  // Skip during prerendering / SSR
  if (typeof window === 'undefined') return null;
  if (navigator.userAgent === 'ReactSnap') return null;

  // document.referrer and the landing URL's params stay the same across
  // client-side navigations, so only the landing page of each page load can
  // be attributed. Later route changes would otherwise log one citation per
  // page viewed.
  if (hasCheckedThisPageLoad) return null;
  hasCheckedThisPageLoad = true;

  // Debug logging — remove after confirming Bing tracking works
  console.log('[AI Citation Debug] document.referrer:', document.referrer);
  console.log('[AI Citation Debug] location:', window.location.href);

  const detection = detectAICitation();
  console.log('[AI Citation Debug] detection result:', detection);
  if (!detection) return null;

  // Track both human click-throughs AND AI bot/crawler visits
  // The dashboard will filter them separately by method (referrer/url-param vs user-agent)

  // Build citation record
  const citation = {
    // Detection info
    source: detection.source,
    engine: detection.engine,
    method: detection.method,
    referrer: detection.referrer || document.referrer || '',
    botType: detection.botType || null,
    param: detection.param || null,
    value: detection.value || null,

    // Page info
    pagePath: window.location.pathname,
    pageUrl: window.location.href,
    pageTitle: document.title,
    pageType: classifyPage(window.location.pathname),

    // Timing
    timestamp: new Date().toISOString(),
    sessionId: getSessionId(),

    // Extra context
    userAgent: navigator.userAgent,
    screenWidth: window.screen?.width,
    language: navigator.language,
  };

  // Mark this session as AI-attributed (first-touch — don't overwrite an
  // earlier detection in the same session with a later one) so that any
  // affiliate link clicked later in the session can be tied back to it.
  if (!sessionStorage.getItem(SESSION_ATTRIBUTION_KEY)) {
    sessionStorage.setItem(SESSION_ATTRIBUTION_KEY, JSON.stringify({
      source: citation.source,
      engine: citation.engine,
      method: citation.method,
      referrer: citation.referrer,
      landingPage: citation.pagePath,
      timestamp: citation.timestamp,
    }));
  }

  // Deduplicate: Don't record the same source + page + session twice
  const dedupeKey = `${citation.source}|${citation.pagePath}|${citation.sessionId}`;
  const lastDedupeKey = sessionStorage.getItem('os_ai_citation_last');
  if (lastDedupeKey === dedupeKey) {
    return citation; // Already tracked this visit
  }
  sessionStorage.setItem('os_ai_citation_last', dedupeKey);

  // Save locally
  saveCitation(citation);

  // Send to backend
  sendCitationBeacon(citation);

  return citation;
}

/**
 * Read the AI source that was first detected for the current browser session
 * (set by trackAICitation). Used to tell whether a later affiliate click in
 * this session should be attributed to AI-driven traffic.
 * @returns {{ source: string, engine: string, method: string, referrer: string, landingPage: string, timestamp: string } | null}
 */
export function getAISessionAttribution() {
  try {
    const raw = sessionStorage.getItem(SESSION_ATTRIBUTION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

/**
 * Record an affiliate link click as a conversion for the current AI-attributed
 * session. No-ops if this session was never detected as AI-driven — regular
 * (non-AI) affiliate clicks are already covered by useGlobalOutboundTracking's
 * GA4 events, this is only for tying conversions back to an AI source.
 *
 * Reuses the same local storage + beacon pipeline as trackAICitation, so no
 * new backend endpoint or schema is required.
 *
 * @param {{ platform: string, url: string, linkText?: string }} affiliate
 * @returns {object | null} The recorded event, or null if not an AI session
 */
export function trackAffiliateClick(affiliate) {
  if (typeof window === 'undefined') return null;

  const attribution = getAISessionAttribution();
  if (!attribution) return null;

  const citation = {
    // Attribution carried over from the original AI detection
    source: attribution.source,
    engine: attribution.engine,
    method: 'affiliate-click',
    referrer: attribution.referrer,
    botType: null,
    param: 'affiliate_platform',
    value: affiliate.platform,

    // What was clicked, and where
    affiliateUrl: affiliate.url,
    affiliatePlatform: affiliate.platform,
    linkText: affiliate.linkText || '',
    landingPage: attribution.landingPage,
    pagePath: window.location.pathname,
    pageUrl: window.location.href,
    pageTitle: document.title,
    pageType: classifyPage(window.location.pathname),

    // Timing
    timestamp: new Date().toISOString(),
    sessionId: getSessionId(),

    userAgent: navigator.userAgent,
    screenWidth: window.screen?.width,
    language: navigator.language,
  };

  saveCitation(citation);
  sendCitationBeacon(citation);

  return citation;
}


// ─── Helper Functions ──────────────────────────────────────────────────────────

/**
 * Classify a URL path into a page type for aggregate reporting
 */
function classifyPage(pathname) {
  if (pathname.includes('/blog/') || pathname.includes('/blog')) return 'blog';
  if (pathname.includes('/products/') || pathname.includes('/product/')) return 'product';
  if (pathname.includes('/brands/') || pathname.includes('/brand/')) return 'brand';
  if (pathname.includes('/values/')) return 'values';
  if (pathname.includes('/search')) return 'search';
  if (pathname.includes('/hub/') || pathname.includes('/hub')) return 'hub';
  if (pathname.includes('/home') || pathname === '/' || pathname === '') return 'home';
  if (pathname.includes('/about')) return 'about';
  if (pathname.includes('/register') || pathname.includes('/pricing')) return 'register';
  return 'other';
}

/**
 * Session ID using sessionStorage (cookieless)
 */
function getSessionId() {
  let sid = sessionStorage.getItem('os_ai_session');
  if (!sid) {
    sid = 'ais_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
    sessionStorage.setItem('os_ai_session', sid);
  }
  return sid;
}


// ─── Export Utilities ──────────────────────────────────────────────────────────

/**
 * Export citations as CSV for analysis
 * @param {Array} [citations] - Citations to export. Defaults to this browser's
 *   local rolling log (last 500 events) when omitted; pass the dashboard's
 *   server-fetched event list to export the full multi-visitor dataset instead.
 * @returns {string} CSV content
 */
export function exportCitationsCSV(citations = getStoredCitations()) {
  if (citations.length === 0) return '';

  const headers = ['timestamp', 'source', 'engine', 'method', 'pagePath', 'pageType', 'pageTitle', 'referrer', 'botType', 'param', 'value', 'affiliatePlatform', 'affiliateUrl', 'landingPage'];
  const rows = citations.map(c =>
    headers.map(h => `"${(c[h] || '').toString().replace(/"/g, '""')}"`).join(',')
  );

  return [headers.join(','), ...rows].join('\n');
}

/**
 * Download citations as CSV file
 * @param {Array} [citations] - See exportCitationsCSV.
 */
export function downloadCitationsCSV(citations) {
  const csv = exportCitationsCSV(citations);
  if (!csv) {
    console.warn('[AI Citation Tracker] No citations to export');
    return;
  }

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `originselect-ai-citations-${new Date().toISOString().split('T')[0]}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

/**
 * Clear all stored citation data (useful for testing)
 */
export function clearCitations() {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(STORAGE_STATS_KEY);
  sessionStorage.removeItem('os_ai_citation_last');
  console.log('[AI Citation Tracker] All citation data cleared');
}

/**
 * Get a quick summary for console/debugging
 */
export function getCitationSummary() {
  const stats = getCitationStats();
  const citations = getStoredCitations();

  return {
    total: stats.totalCitations,
    storedEvents: citations.length,
    trackingSince: stats.firstSeen,
    lastDetected: stats.lastSeen,
    topSources: Object.entries(stats.bySource)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10),
    topPages: Object.entries(stats.byPage)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10),
    byEngine: stats.byEngine,
  };
}
